import { Injectable } from '@angular/core';
import { ReportEntryDto, ReportEntryValue } from '@osos/shared/api';
import {
  ApplicationSharingLevelEnum,
  DataEntrySourceModel,
  ReportEntryValuePresenterModel,
  ReportFormatEnum,
  ReportPresenterModel,
  SendReportEmailActionOptionsModel,
} from '@osos/shared/types';
import { ParserBase } from '../../bases';

@Injectable({
  providedIn: 'root',
})
export class ReportsParserService extends ParserBase<
  ReportEntryDto,
  ReportPresenterModel
> {
  constructor() {
    super(ReportEntryDto, ReportPresenterModel, 1);
  }

  getPresenterModel(model: Partial<ReportEntryDto>): ReportPresenterModel {
    return {
      creationInfo: model.creationInfo,
      dataEntity: model.dataEntity,
      id: model.id,
      permissions: model.permissions,
      specificationVersion: this.version,
      state: model.state,
      stateHistory: model.stateHistory,
      updateInfo: model.updateInfo,
      value: this._getReportEntryValuePresenter(model.value),
    };
  }

  getDataModel(presenter: ReportPresenterModel): ReportEntryDto {
    return {
      creationInfo: undefined,
      updateInfo: undefined,
      dataEntity: presenter.dataEntity,
      permissions: presenter.permissions,
      specificationVersion: this.version,
      state: presenter.state,
      isInvalid: false,
      value: this._getReportEntryValueDataModel(presenter.value),
    };
  }

  private _getReportEntryValuePresenter(
    entryValue: ReportEntryValue,
  ): ReportEntryValuePresenterModel {
    const emailNotification = entryValue.emailNotification;
    const presentedEmailNotification: SendReportEmailActionOptionsModel = {
      ...emailNotification,
      attachment: {
        enabled: emailNotification.attachment?.enabled,
        type: emailNotification.attachment?.type
          ? emailNotification.attachment.type === 'xlsx'
            ? ReportFormatEnum.XLSX
            : ReportFormatEnum.PDF
          : undefined,
      },
    };
    return {
      name: entryValue.name,
      authenticated: entryValue.authenticated,
      dataSource: entryValue.dataSource,
      description: entryValue.description,
      fields: entryValue.fields,
      icon: entryValue.icon,
      isPublished: entryValue.isPublished,
      allowChangeFields: entryValue.allowChangeFields,
      relatedTo: entryValue.relatedTo,
      displayOrder: entryValue.displayOrder,
      schedule: entryValue.schedule,
      emailNotification: presentedEmailNotification,
      design: entryValue.design,
      domain: entryValue.domain,
      sharing: {
        ...entryValue.sharing,
        level: entryValue.sharing.level.id as ApplicationSharingLevelEnum,
      },
      help: entryValue.help,
    };
  }

  private _getReportEntryValueDataModel(
    entryValue: ReportEntryValuePresenterModel,
  ): ReportEntryValue {
    return {
      name: entryValue.name,
      authenticated: entryValue.authenticated,
      dataSource: entryValue.dataSource as Required<DataEntrySourceModel>,
      description: entryValue.description,
      fields: entryValue.fields,
      icon: entryValue.icon?.name ? entryValue.icon : undefined,
      isPublished: entryValue.isPublished,
      allowChangeFields: entryValue.allowChangeFields,
      relatedTo: entryValue.relatedTo,
      displayOrder:
        entryValue.displayOrder !== null ? entryValue.displayOrder : undefined,
      schedule: entryValue.schedule,
      emailNotification: entryValue.emailNotification,
      design: entryValue.design,
      domain: entryValue.domain,
      sharing: {
        ...entryValue.sharing,
        level: {
          id: entryValue.sharing.level,
        },
      },
      help: entryValue.help?.contextualHelp ? entryValue.help : null,
    };
  }
}
